import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { canBeBillable, dayKey, entryInvoice, formatMoney, entryEarnings, isTaskOpen, orderedTasks, taskLabel, type Dataset } from "@otrack/domain";
import { Button, DateField, FieldError, Icon, IconButton, Segmented, Select, Switch, TextInput, TimeField, Tooltip, type SelectOption } from "@otrack/ui";
import { projectLabel, projectRefs } from "../commands";
import { useApp } from "../context";
import { href } from "../router";
import { formatClock, formatDuration, fromTimeInput, keepExact, startOfDay, toTimeInput } from "../time";
import type { Destination, Draft, useCalendarCommands } from "./actions";
import { writableCalendars } from "./google";
import { colorOf, isEditablePlan, isEntry, type CalItem, type PlanItem } from "./model";

type Commands = ReturnType<typeof useCalendarCommands>;

export function projectOptions(dataset: Dataset, current: string | null): Array<SelectOption<string>> {
  const refs = projectRefs(dataset);
  if (current && !refs.some((ref) => ref.id === current) && dataset.projects[current]) {
    refs.push({ id: current, name: dataset.projects[current].name, clientName: null });
  }
  return [
    { value: "", label: "No project", swatch: colorOf(dataset, null) },
    ...refs.map((ref) => ({ value: ref.id, label: ref.name, hint: ref.clientName ?? undefined, swatch: colorOf(dataset, ref.id) }))
  ];
}

export function taskOptions(dataset: Dataset, projectId: string, current: string | null): Array<SelectOption<string>> {
  const tasks = orderedTasks(dataset, projectId, (task) => isTaskOpen(dataset, task) || task.id === current);
  return tasks.length ? [{ value: "", label: "No task" }, ...tasks.map((task) => ({ value: task.id, label: taskLabel(dataset, task), icon: task.parentId ? "subtask" as const : "target" as const }))] : [];
}

const dayFormat = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

const ONTRACK = "ontrack";

/** Where a new event can go: ontrack, or a Google calendar ontrack may add events to. */
export function destinationOptions(calendars: ReadonlyArray<{ id: string; name: string; color: string; accessRole: string }>): Array<SelectOption<string>> {
  return [
    { value: ONTRACK, label: "ontrack", hint: "Only here", icon: "calendar" },
    ...writableCalendars(calendars as Parameters<typeof writableCalendars>[0]).map((calendar) => ({ value: calendar.id, label: calendar.name, hint: "Google Calendar", swatch: calendar.color }))
  ];
}

/** The destination Settings picks for new events, when it can still take them. */
export function defaultDestination(chosen: string | null, options: ReadonlyArray<SelectOption<string>>): string {
  return chosen && options.some((option) => option.value === chosen) ? chosen : ONTRACK;
}

export const destinationOf = (value: string): Destination => (value === ONTRACK ? null : value);

/** Keeps the panel beside its anchor: right if there is room, else left, else below or above. */
function useBesidePosition(anchor: () => HTMLElement | null, panel: RefObject<HTMLDivElement | null>, deps: unknown[], pressedAt?: () => number | null) {
  const [position, setPosition] = useState<{ left: number; top: number; side: "left" | "right" | "below" | "above" } | null>(null);
  useLayoutEffect(() => {
    const update = () => {
      const target = anchor();
      const element = panel.current;
      if (!target || !element) return;
      const block = target.getBoundingClientRect();
      const { width, height } = element.getBoundingClientRect();
      // A block taller than the panel anchors at the spot that was pressed, not at its far ends.
      const offset = pressedAt?.() ?? null;
      const a = offset !== null && block.height > height ? new DOMRect(block.left, block.top + Math.min(Math.max(offset - 14, 0), block.height - 28), block.width, 28) : block;
      const gap = 10, pad = 10;
      const top = Math.min(Math.max(pad, a.top), Math.max(pad, window.innerHeight - height - pad));
      if (a.right + gap + width + pad <= window.innerWidth) return setPosition({ left: a.right + gap, top, side: "right" });
      if (a.left - gap - width >= pad) return setPosition({ left: a.left - gap - width, top, side: "left" });
      // Narrow screens: under the block, or over it, so the block itself stays in view.
      const left = Math.min(Math.max(pad, a.left), Math.max(pad, window.innerWidth - width - pad));
      if (a.bottom + gap + height + pad <= window.innerHeight) return setPosition({ left, top: a.bottom + gap, side: "below" });
      if (a.top - gap - height >= pad) return setPosition({ left, top: a.top - gap - height, side: "above" });
      setPosition({ left, top: Math.max(pad, window.innerHeight - height - pad), side: "below" });
    };
    update();
    window.addEventListener("resize", update);
    document.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      document.removeEventListener("scroll", update, true);
    };
    // The anchor is looked up again whenever what it points at changes.
  }, deps);
  return position;
}

export type PopoverTarget = { mode: "draft"; draft: Draft } | { mode: "item"; key: string };

/**
 * The quick editor beside a block: what most edits need, saved with Enter. "More options" opens
 * the full editor for the rest (repeats, notes, invoice details).
 */
export function CalendarPopover({ target, item, dataset, commands, anchor, pressedAt, onClose, onDraftKind, onMore }: {
  target: PopoverTarget;
  item: CalItem | null;
  dataset: Dataset;
  commands: Commands;
  anchor: () => HTMLElement | null;
  /** How far down the pressed block the press landed, when it was pressed. */
  pressedAt?: () => number | null;
  onClose: () => void;
  onDraftKind: (kind: Draft["kind"]) => void;
  onMore: (plan: PlanItem | null, draft: (Draft & { title: string; projectId: string | null; taskId: string | null; destination: Destination }) | null) => void;
}) {
  const { now, openEntry, google } = useApp();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const draft = target.mode === "draft" ? target.draft : null;
  const kind: "entry" | "event" | "google" = draft ? draft.kind : item ? (isEntry(item) ? "entry" : item.kind) : "event";
  const base = draft ?? item!;
  const entryItem = item && isEntry(item) ? item : null;
  const plan = item && !isEntry(item) ? item : null;
  const external = plan !== null && !isEditablePlan(plan);
  const readOnly = external || (entryItem?.locked ?? false);
  const destinations = useMemo(() => destinationOptions(google?.store.getState().calendars ?? []), [google]);
  const [destination, setDestination] = useState(() => defaultDestination(dataset.settings.newEventsCalendar, destinations));
  const running = entryItem?.running ?? false;

  const [title, setTitle] = useState(() => item?.title ?? "");
  const [projectId, setProjectId] = useState(() => item?.projectId ?? "");
  const [taskId, setTaskId] = useState(() => item?.taskId ?? "");
  const [day, setDay] = useState(() => startOfDay(base.start));
  const [start, setStart] = useState(() => toTimeInput(base.start));
  const [end, setEnd] = useState(() => (running ? "" : toTimeInput(base.end)));
  const [allDay, setAllDay] = useState(() => draft?.allDay ?? plan?.allDay ?? false);
  const [billable, setBillable] = useState(() => entryItem?.billable ?? true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // TimeField settles on blur and Enter; reading through refs keeps Enter-to-save exact.
  const times = useRef({ start, end });
  times.current = { start, end };

  // A draft keeps following the block when it is redrawn (Entry/Event switch, resize).
  useEffect(() => {
    if (!draft) return;
    setDay(startOfDay(draft.start));
    setStart(toTimeInput(draft.start));
    setEnd(toTimeInput(draft.end));
    setAllDay(draft.allDay);
  }, [draft?.start, draft?.end, draft?.allDay]);

  useEffect(() => {
    if (!readOnly) titleRef.current?.focus({ preventScroll: true });
    else panelRef.current?.focus({ preventScroll: true });
  }, [readOnly, target.mode]);

  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      const node = event.target as HTMLElement;
      if (panelRef.current?.contains(node) || node.closest?.(".ot-floating, .ot-modal-backdrop")) return;
      if (anchor()?.contains(node)) return;
      onClose();
    };
    document.addEventListener("pointerdown", dismiss, true);
    return () => document.removeEventListener("pointerdown", dismiss, true);
  }, [anchor, onClose]);

  const position = useBesidePosition(anchor, panelRef, [base.start, base.end, kind, target.mode === "item" ? target.key : "draft"], pressedAt);
  const project = projectId ? dataset.projects[projectId] : undefined;
  const projects = useMemo(() => projectOptions(dataset, item?.projectId ?? null), [dataset, item?.projectId]);
  const tasks = projectId ? taskOptions(dataset, projectId, item?.taskId ?? null) : [];
  const billableAllowed = canBeBillable(project);
  const future = base.start >= now;

  const span = (): { start: number; end: number } | string => {
    if (allDay) return { start: day, end: startOfDay(day, Math.max(1, Math.round((base.end - base.start) / 86_400_000))) };
    // An entry keeps its exact edges while their fields are left alone.
    const exact = entryItem?.entry ?? null;
    const from = keepExact(fromTimeInput(day, times.current.start), exact?.start ?? null);
    if (from === null) return "Enter a start time.";
    if (running && !times.current.end) return { start: from, end: Date.now() };
    let to = keepExact(fromTimeInput(day, times.current.end), exact?.end ?? null);
    if (to === null) return "Enter an end time.";
    // An end before the start runs past midnight.
    if (to <= from) to = keepExact(fromTimeInput(startOfDay(day, 1), times.current.end), exact?.end ?? null)!;
    return { start: from, end: to };
  };

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    if (readOnly || busy) return;
    const times = span();
    if (typeof times === "string") return setError(times);
    setBusy(true);
    try {
      let problem: string | null = null;
      if (draft) {
        problem = await commands.createDraft({ ...draft, start: times.start, end: times.end, allDay: draft.kind === "event" && allDay }, { title: title.trim(), projectId: projectId || null, taskId: taskId || null, billable, destination: destinationOf(destination) });
      } else if (entryItem) {
        problem = await commands.saveEntry(entryItem, { title: title.trim(), projectId: projectId || null, taskId: taskId || null, start: times.start, ...(running ? {} : { end: times.end }), billable });
      } else if (plan) {
        const patch: Record<string, unknown> = {};
        if (title.trim() !== plan.title) patch["title"] = title.trim();
        if ((projectId || null) !== plan.projectId) { patch["projectId"] = projectId || null; patch["taskId"] = taskId || null; }
        else if ((taskId || null) !== plan.taskId) patch["taskId"] = taskId || null;
        if (allDay !== plan.allDay) patch["allDay"] = allDay;
        if (allDay) {
          if (times.start !== plan.start || allDay !== plan.allDay) Object.assign(patch, { allDay: true, startDay: dayKey(times.start), endDay: dayKey(times.end - 1) });
        } else if (times.start !== plan.start || times.end !== plan.end) Object.assign(patch, { start: times.start, end: times.end });
        if (Object.keys(patch).length) {
          const done = await commands.changePlan(plan, patch, `Updated ${title.trim() || "the event"}`);
          if (!done) return;
        }
      }
      if (problem) return setError(problem);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Escape") { event.preventDefault(); onClose(); }
    else if (event.key === "Enter" && !event.shiftKey && (event.target as HTMLElement).tagName === "INPUT") {
      // Enter saves from any text field, after a time field has settled what was typed.
      event.preventDefault();
      requestAnimationFrame(() => void save());
    }
    else if (draft && event.altKey && event.key.toLowerCase() === "e" && !future) { event.preventDefault(); onDraftKind(draft.kind === "entry" ? "event" : "entry"); }
  };

  const heading = draft ? (draft.kind === "entry" ? "New time entry" : "New event") : kind === "entry" ? (running ? "Running timer" : "Time entry") : plan?.google ? plan.google.calendar.name : plan?.recurring ? "Repeating event" : "Event";
  const details = plan?.google?.occurrence.event;
  const guests = (details?.attendees ?? []).filter((person) => !person.self);
  const invoice = entryItem ? entryInvoice(dataset, entryItem.entry) : undefined;
  const logged = plan ? plan.logged.reduce((sum, entry) => sum + Math.max(0, (entry.end ?? now) - entry.start), 0) : 0;
  const earned = entryItem ? entryEarnings(entryItem.entry, now) : null;
  const preview = span();
  const length = typeof preview === "string" ? null : preview;

  return createPortal(<motion.div
    ref={panelRef}
    className="ot-calpop"
    role="dialog"
    aria-label={heading}
    tabIndex={-1}
    data-modal-escape-boundary="true"
    data-side={position?.side ?? "right"}
    style={{ ["--pc" as string]: colorOf(dataset, projectId || null), left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? "visible" : "hidden" }}
    initial={{ opacity: 0, scale: 0.97, x: position?.side === "left" ? 6 : -6 }}
    animate={{ opacity: 1, scale: 1, x: 0 }}
    transition={{ type: "spring", stiffness: 560, damping: 40 }}
    onKeyDown={onKeyDown}
  >
    <form onSubmit={(event) => void save(event)} noValidate>
      <header className="ot-calpop__head">
        <i className="ot-swatch" aria-hidden="true"/>
        <span className="ot-calpop__kind">{heading}</span>
        {plan?.recurring && <Tooltip label="Repeats"><span className="ot-calpop__badge"><Icon name="repeat"/></span></Tooltip>}
        {entryItem?.locked && <Tooltip label="On a sent invoice"><span className="ot-calpop__badge"><Icon name="lock"/></span></Tooltip>}
        <IconButton type="button" className="is-ghost ot-calpop__close" label="Close" icon="close" tooltip={false} onClick={onClose}/>
      </header>

      {draft && !future && <Segmented
        stretch
        size="sm"
        aria-label="Kind"
        value={draft.kind}
        onChange={onDraftKind}
        options={[{ value: "entry", label: "Time entry", icon: "clock" }, { value: "event", label: "Event", icon: "calendar" }]}
      />}

      {readOnly ? <div className="ot-calpop__readonly">
        <strong>{item?.title || projectLabel(dataset, item?.projectId ?? null)}</strong>
        <span className="ot-mono">{plan?.allDay ? "All day" : `${dayFormat.format(base.start)}, ${formatClock(base.start)} to ${formatClock(base.end)}`}</span>
        {plan?.google?.location && <span className="ot-calpop__detail"><Icon name="location"/>{plan.google.location}</span>}
        {guests.length > 0 && <span className="ot-calpop__detail"><Icon name="user"/>{guests.length === 1 ? guests[0]!.displayName || guests[0]!.email : `${guests.length} guests`}{details?.organizer?.displayName || details?.organizer?.email ? `, organized by ${details.organizer.displayName || details.organizer.email}` : ""}</span>}
        {plan?.notes && <p>{plan.notes.length > 280 ? `${plan.notes.slice(0, 280)}…` : plan.notes}</p>}
        {external && <p className="ot-calpop__note"><Icon name="lock"/>{guests.length ? "An invitation: only its organizer can change it." : "On a calendar ontrack can only read."}</p>}
        {entryItem?.locked && invoice && <p className="ot-calpop__note"><Icon name="lock"/>On <a href={href({ page: "invoice", id: invoice.id })}>{invoice.number ?? "a sent invoice"}</a>. Move it back to draft to change this entry.</p>}
      </div> : <>
        <TextInput
          ref={titleRef}
          aria-label={kind === "entry" ? "Description" : "Title"}
          className="ot-calpop__title"
          value={title}
          maxLength={kind === "entry" ? 1000 : 200}
          placeholder={kind === "entry" ? "What did you work on?" : "Add a title"}
          onChange={(event) => { setTitle(event.target.value); setError(null); }}
        />
        <div className="ot-calpop__when">
          {allDay ? <DateField aria-label="Day" value={day} onChange={(next) => { setDay(next); setError(null); }}/> : <>
            <DateField aria-label="Day" value={day} onChange={(next) => { setDay(next); setError(null); }} {...(kind === "entry" ? { max: now } : {})}/>
            <div className="ot-calpop__times">
              <TimeField aria-label="Start" value={start} onChange={(next) => { setStart(next); setError(null); }}/>
              <span aria-hidden="true">→</span>
              <TimeField aria-label="End" value={end} placeholder={running ? "Now" : "10:00"} onChange={(next) => { setEnd(next); setError(null); }}/>
            </div>
          </>}
          {length && !allDay && <span className="ot-calpop__length ot-mono">{formatDuration(length.end - length.start)}</span>}
        </div>
      </>}

      {draft?.kind === "event" && destinations.length > 1 && <Select aria-label="Calendar" value={destination} onChange={setDestination} options={destinations}/>}

      {!external && !readOnly && <Select
        aria-label="Project"
        value={projectId}
        searchable={projects.length > 7}
        searchPlaceholder="Find a project"
        onChange={(next) => { setProjectId(next); setTaskId(""); }}
        options={projects}
      />}
      {!external && !readOnly && tasks.length > 0 && <Select aria-label="Task" value={taskId} onChange={setTaskId} options={tasks}/>}
      {external && <Select aria-label="Project for logged time" value={projectId} searchable={projects.length > 7} onChange={setProjectId} options={[{ ...projects[0]!, label: "Choose a project" }, ...projects.slice(1)]}/>}

      {kind === "entry" && !readOnly && <Switch label="Billable" checked={billableAllowed && billable} disabled={!billableAllowed} onChange={setBillable}/>}
      {kind !== "entry" && !readOnly && <Switch label="All day" checked={allDay} onChange={setAllDay}/>}

      {plan && !draft && <div className="ot-calpop__plan">
        {plan.logged.length > 0 && <span className="ot-calpop__logged"><Icon name="check-circle" weight="bold"/>Logged <span className="ot-mono">{formatDuration(logged)}</span>{!plan.allDay && <> of <span className="ot-mono">{formatDuration(plan.end - plan.start)}</span></>}</span>}
        <div className="ot-calpop__plan-actions">
          {!plan.allDay && plan.start < now && <Button type="button" icon="check" onClick={() => { onClose(); void commands.logPlan(plan, projectId || plan.projectId); }}>Log as worked</Button>}
          <Button type="button" tone="accent" icon="play" onClick={() => { onClose(); void commands.startPlan(plan, projectId || plan.projectId); }}>Start timer</Button>
        </div>
      </div>}

      {entryItem && !draft && <div className="ot-calpop__facts ot-mono">
        {running ? <span className="is-live">Running {formatDuration(now - entryItem.start)}</span> : <span>{formatDuration(entryItem.end - entryItem.start)}</span>}
        {earned && <span>{formatMoney(earned)}</span>}
        {invoice && !entryItem.locked && <a href={href({ page: "invoice", id: invoice.id })}>On a draft invoice</a>}
      </div>}

      <FieldError>{error}</FieldError>

      <footer className="ot-calpop__foot">
        {!draft && !readOnly && <IconButton type="button" className="is-ghost" label={kind === "entry" ? "Move to Trash" : "Delete event"} icon="trash" onClick={() => { onClose(); if (item) void commands.removeItems([item]); }}/>}
        {!draft && entryItem && <Button type="button" tone="ghost" onClick={() => { onClose(); openEntry(entryItem.entry.id); }}>Details</Button>}
        {plan && !external && <Button type="button" tone="ghost" onClick={() => { onClose(); onMore(plan, null); }}>More options</Button>}
        {details?.htmlLink && <a className="ot-button ot-button--ghost" href={details.htmlLink} target="_blank" rel="noopener noreferrer">Open in Google</a>}
        {draft?.kind === "event" && <Button type="button" tone="ghost" onClick={() => {
          if (typeof preview === "string") return setError(preview);
          onClose();
          onMore(null, { ...draft, ...preview, allDay, title: title.trim(), projectId: projectId || null, taskId: taskId || null, destination: destinationOf(destination) });
        }}>More options</Button>}
        <span className="ot-form__spacer"/>
        {running && <Button type="button" tone="accent" icon="stop" onClick={() => { onClose(); void commands.stop(); }}>Stop</Button>}
        {!readOnly && <Button type="submit" tone="primary" disabled={busy}>Save</Button>}
      </footer>
    </form>
  </motion.div>, document.body);
}
